import { useAppUserDefaultStore } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useRevenueHistory } from '../../../stores/appRevenueHistoryStore';
import { useCostPriceStore } from '../../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import type { BackendUser } from '../../../services/user/userService';
import { useTimeIntervalStore } from '../../../stores/appTimeIntervalStore';
import { computeUtilizationReport } from '../../../utils/reports/utilizationReport';
import { computeInventoryVarianceAll } from '../../../utils/reports/inventoryVariance';
import { rangeFromTimeControls } from '../../../utils/reports/shared';
import { resolveWastePercentage } from '../../../utils/wasteUtils';
import { startOfPeriod, calculateEndDate } from '../../../utils/dateUtils';
import { isPracticeId } from './practiceGuards';

export type DataTourKind = 'utilization' | 'inventory' | 'leakage';
export type DataTourSource = 'customer' | 'fixture';
type Observable<S> = { getState: () => S; subscribe: (listener: () => void) => () => void };

/** Store loaders can return early while a page's request is still in flight. */
function waitUntilIdle<S>(store: Observable<S>, pending: (state: S) => boolean, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    let unsubscribe = () => {};
    const finish = () => {
      if (!signal.aborted && pending(store.getState())) return;
      unsubscribe();
      signal.removeEventListener('abort', finish);
      if (signal.aborted) reject(new Error('Tour preparation cancelled'));
      else resolve();
    };
    unsubscribe = store.subscribe(finish);
    signal.addEventListener('abort', finish, { once: true });
    finish();
  });
}

/** A failed or incomplete load is never evidence that an account has no data. */
export async function prepareDataTour(
  kind: DataTourKind,
  restaurantId: string,
  allowFixture: boolean,
  signal: AbortSignal,
  backendUser: BackendUser | null,
): Promise<{ source: DataTourSource; allTime?: boolean } | null> {
  const current = () => !signal.aborted && useAppUserDefaultStore.getState().restaurant?.id === restaurantId;
  const requireCurrent = () => { if (!current()) throw new Error('Tour restaurant changed'); };
  try {
    requireCurrent();
    await waitUntilIdle(useRestaurantStore, s => s.isLoading, signal);
    requireCurrent();
    // Root mounts before some page effects. Start the ordinary metadata loader
    // ourselves when bootstrap has not yet requested it.
    if (!useRestaurantStore.getState().isDataLoaded) await useRestaurantStore.getState().loadAllData();
    requireCurrent();
    if (!useRestaurantStore.getState().isDataLoaded) return null;
    const restaurant = useRestaurantStore.getState();
    const platformIds = (restaurant.restaurantSections[restaurantId] ?? [])
      .flatMap(section => (restaurant.restaurantSectionPlatforms[section.id] ?? []).map(platform => platform.id)).sort();
    const revenueSourceIds = (restaurant.restaurantRevenueSources[restaurantId] ?? []).map(source => source.id).sort();
    requireCurrent();
    // Use the same scoped/enriched loaders as the page hooks, including their
    // full-history completion guarantees after a progressive dashboard load.
    await Promise.all([
      platformIds.length ? useOrderHistory.getState().loadByPlatformIds(restaurantId, platformIds, restaurant.platformToSectionIndex, backendUser)
        : Promise.resolve(useOrderHistory.getState().markOrderhistoryEmptyForRestaurant(restaurantId)),
      revenueSourceIds.length ? useRevenueHistory.getState().loadByRevenueSourceIds(restaurantId, revenueSourceIds, restaurant.restaurantRevenueSources)
        : Promise.resolve(useRevenueHistory.getState().markRevenuehistoryEmptyForRestaurant(restaurantId)),
      (async () => {
        await waitUntilIdle(useCostPriceStore, s => s.isLoading, signal);
        requireCurrent();
        if (!useCostPriceStore.getState().isDataLoadedFor(restaurantId)) await useCostPriceStore.getState().load(restaurantId, { isCurrent: current });
        await waitUntilIdle(useCostPriceStore, s => s.isLoading || s.isLoadingCustomIngredients, signal);
        requireCurrent();
        if (!useCostPriceStore.getState().isCustomIngredientsLoadedFor(restaurantId)) await useCostPriceStore.getState().loadCustomIngredients(restaurantId);
      })(),
      (async () => {
        await waitUntilIdle(useSubrecipesStore, s => s.isLoading, signal);
        requireCurrent();
        if (!useSubrecipesStore.getState().isDataLoadedFor(restaurantId)) await useSubrecipesStore.getState().load(restaurantId, { isCurrent: current });
      })(),
    ]);
    requireCurrent();
    const orders = useOrderHistory.getState();
    const revenue = useRevenueHistory.getState();
    const cost = useCostPriceStore.getState();
    const subrecipes = useSubrecipesStore.getState();
    if (orders.currentRestaurantId !== restaurantId || !orders.fullyLoaded || orders.isLoading || orders.isBackgroundLoading ||
        revenue.currentRestaurantId !== restaurantId || !revenue.fullyLoaded || revenue.isLoading || revenue.isBackgroundLoading ||
        !cost.isDataLoadedFor(restaurantId) || !cost.isCustomIngredientsLoadedFor(restaurantId) || cost.error || cost.customIngredientsError ||
        !subrecipes.isDataLoadedFor(restaurantId)) return null;
    if (kind === 'utilization') {
      const time = useTimeIntervalStore.getState();
      const input = {
        orders: orders.orderHistoryList.filter(order => !isPracticeId(order.id)),
        revenue: revenue.revenueHistoryList,
        costPriceList: cost.costPriceList.filter(dish => !isPracticeId(dish.id)),
        subrecipesById: subrecipes.subrecipesById,
        currentRestaurantId: restaurantId,
        defaultWastePercentage: resolveWastePercentage(restaurant.reportSettings[restaurantId]?.wastePercentage),
        range: rangeFromTimeControls(time.view, time.startDate, time.endDate),
      };
      const report = computeUtilizationReport(input);
      if (report.hasMappableCostPrices && report.rows.length) return { source: 'customer' };
      const allStart = startOfPeriod(new Date(), 'all');
      const allTime = computeUtilizationReport({ ...input, range: rangeFromTimeControls('all', allStart, calculateEndDate(allStart, 'all')) });
      if (allTime.hasMappableCostPrices && allTime.rows.length) return { source: 'customer', allTime: true };
      return allowFixture ? { source: 'fixture' } : null;
    }

    await waitUntilIdle(useInventoryStore, s => s.isLoadingSections || s.isLoadingCounts || s.countReview.isLoading, signal);
    requireCurrent();
    if (useInventoryStore.getState().currentRestaurantId !== restaurantId) await useInventoryStore.getState().loadSections(restaurantId);
    await waitUntilIdle(useInventoryStore, s => s.isLoadingSections, signal);
    requireCurrent();
    if (useInventoryStore.getState().error || useInventoryStore.getState().currentRestaurantId !== restaurantId) return null;
    if (useInventoryStore.getState().countsCompleteForRestaurantId !== restaurantId) await useInventoryStore.getState().loadCountsHistory(restaurantId, { complete: true });
    await waitUntilIdle(useInventoryStore, s => s.isLoadingCounts, signal);
    requireCurrent();
    if (useInventoryStore.getState().error || useInventoryStore.getState().countsCompleteForRestaurantId !== restaurantId) return null;
    await useInventoryStore.getState().loadCountReview(restaurantId, { groupId: null, sessionId: null });
    await waitUntilIdle(useInventoryStore, s => s.countReview.isLoading, signal);
    requireCurrent();
    const inventory = useInventoryStore.getState();
    if (inventory.error || !inventory.countReview.loadedKey?.startsWith(`${restaurantId}|`) || !inventory.countReview.data) return null;
    if (kind === 'leakage') {
      await Promise.all(inventory.groups.map(group => useInventoryStore.getState().loadCanonicalUnits(group.id, restaurantId)));
      requireCurrent();
      if (inventory.groups.some(group => !useInventoryStore.getState().canonicalUnitsByGroup[group.id])) return null;
      const leakage = computeInventoryVarianceAll({
        countsHistory: inventory.countsHistory,
        orders: orders.orderHistoryList.filter(order => !isPracticeId(order.id)),
        revenue: revenue.revenueHistoryList,
        costPriceList: cost.costPriceList.filter(dish => !isPracticeId(dish.id)),
        subrecipesById: subrecipes.subrecipesById,
        currentRestaurantId: restaurantId,
        defaultWastePercentage: resolveWastePercentage(restaurant.reportSettings[restaurantId]?.wastePercentage),
        canonicalCountUnits: new Map(Object.entries(useInventoryStore.getState().canonicalUnitsByGroup)),
      });
      if (!leakage.perGroup.length || !leakage.combined.rows.length) return allowFixture ? { source: 'fixture' } : null;
    }
    return inventory.countReview.data.products.length ? { source: 'customer' } : allowFixture ? { source: 'fixture' } : null;
  } catch (error) {
    // Domain stores retain and display their normal load errors. Cancellation and
    // restaurant switches must not publish a fixture or reopen a dismissed tour.
    if (current()) console.error('Failed to prepare guided tour data', error);
    return null;
  }
}
